import { MODAL_ROW_DEFAULTS } from '@gto/announcement-ui'
import type {
  AnnouncementAdminSchemaBlocksItem,
  AnnouncementBlockSpacingChoice,
  AnnouncementCardStyleChoice,
  AnnouncementHeadingSizeChoice,
  AnnouncementMediaKindChoice,
  AnnouncementParagraphStyleChoice,
  AnnouncementRowBlockReadSchemaChildrenItem,
  AnnouncementRowBlockSchemaChildrenItem,
  AnnouncementUpdateRequestBlocksItem,
} from '@gto/api-client/admin/react-query'

import {
  type BlockDraft,
  type BlockDraftId,
  type EmptyBlockDraft,
  type LeafBlockDraft,
  type LeafBlockKind,
  type MediaBlockDraft,
  type MediaBlockLayout,
  type MediaBlockSource,
  mediaPreviewUrl,
  type RowBlockDraft,
  type RowChildBlockDraft,
  type RowChildKind,
  type RowLeafBlockDraft,
} from './block-types'

/**
 * Monotonic within the page's lifetime, which is all identity has to survive:
 * these ids never leave the browser — `toBlocksPayload` strips them — so two
 * tabs handing out `block-3` is not a collision of anything.
 *
 * Deliberately not `crypto.randomUUID`: it makes every test assertion about a
 * draft unwritable without stubbing, for an id whose only requirement is being
 * distinct from its siblings. Reset between tests by `resetBlockDraftIds`.
 *
 * Only ever called from event handlers and from `blockDraftsFrom`, never during
 * render — a counter read while rendering is exactly the kind of side effect the
 * React Compiler is entitled to fold away.
 */
let nextId = 0

export const nextBlockDraftId = (): BlockDraftId => {
  nextId += 1

  return `block-${nextId}`
}

/** Test-only: makes the ids in an assertion predictable. */
export const resetBlockDraftIds = (): void => {
  nextId = 0
}

/**
 * The alignment the CONTRACT applies when the field is omitted — centre for the
 * headline and its copy, left for a bullet list, which reads as a list rather
 * than as a centred poem.
 *
 * Restated here because the editor has to show the operator the value they will
 * get before the server has seen it. Delivery normalises stored blocks and fills
 * these in (C-1843 PR 1), so on the read path this is a fallback that should
 * never fire — kept because the type makes `align` optional and a silent
 * `undefined` would render as neither.
 */
const DEFAULT_ALIGN = {
  heading: 'center',
  paragraph: 'center',
  bullets: 'left',
} as const

/**
 * The styling the CONTRACT applies when a field is omitted, restated for the same
 * reason as `DEFAULT_ALIGN`. Every one is the look a block had before the field
 * existed, so a stored block reads back unchanged.
 */
export const DEFAULT_SPACING: AnnouncementBlockSpacingChoice = 'default'
export const DEFAULT_HEADING_SIZE: AnnouncementHeadingSizeChoice = 'default'
export const DEFAULT_PARAGRAPH_STYLE: AnnouncementParagraphStyleChoice = 'body'
export const DEFAULT_CARD_STYLE: AnnouncementCardStyleChoice = 'default'

/**
 * Which upload the presign endpoint mints for each media block. A hero is an
 * image to the bucket and to every cap the API applies; only its rendering differs.
 */
export const MEDIA_KIND_BY_BLOCK: Record<MediaBlockDraft['type'], AnnouncementMediaKindChoice> = {
  image: 'image',
  video: 'video',
  hero: 'image',
}

/**
 * A media block that names an external link, or nothing at all when the link is blank.
 *
 * The one place the five fields are written out, so a field added to the source
 * cannot be forgotten by a new block, by the read path, or by a test.
 */
export const linkedMediaSource = (url = ''): MediaBlockSource => ({
  url,
  uploadId: '',
  storedUrl: '',
  stagedUrl: '',
  file: null,
})

/**
 * The layout the CONTRACT applies when either field is omitted — the asset fills
 * its container and sits centred, which is how every block stored before C-2163
 * renders. Restated here for the same reason as `DEFAULT_ALIGN`: the operator has
 * to see the value they will get before the server has seen it.
 */
const DEFAULT_MEDIA_LAYOUT = {
  align: 'center',
  width: 'full',
} as const satisfies MediaBlockLayout

/** The one place the layout defaults are written out, for both new and stored blocks. */
export const defaultMediaLayout = (): MediaBlockLayout => ({ ...DEFAULT_MEDIA_LAYOUT })

const mediaLayoutFrom = (block: Partial<MediaBlockLayout>): MediaBlockLayout => ({
  align: block.align ?? DEFAULT_MEDIA_LAYOUT.align,
  width: block.width ?? DEFAULT_MEDIA_LAYOUT.width,
})

/**
 * Everything a media block carries besides its id and its type.
 *
 * The asset and the layout are separate concerns with separate defaults, and
 * every media block needs both, so this is the one place they are put together.
 */
export const mediaBlockFields = (url = ''): MediaBlockSource & MediaBlockLayout => ({
  ...linkedMediaSource(url),
  ...defaultMediaLayout(),
})

export function emptyBlockDraft(kind: RowLeafBlockDraft['type']): RowLeafBlockDraft
export function emptyBlockDraft(kind: LeafBlockKind): LeafBlockDraft
export function emptyBlockDraft(kind: LeafBlockKind): LeafBlockDraft {
  const id = nextBlockDraftId()

  const spacing = DEFAULT_SPACING

  switch (kind) {
    case 'hero':
      return { id, type: 'hero', ...linkedMediaSource(), spacing }
    case 'heading':
      return {
        id,
        type: 'heading',
        text: '',
        highlight: '',
        align: DEFAULT_ALIGN.heading,
        size: DEFAULT_HEADING_SIZE,
        spacing,
      }
    case 'paragraph':
      return {
        id,
        type: 'paragraph',
        text: '',
        highlight: '',
        align: DEFAULT_ALIGN.paragraph,
        style: DEFAULT_PARAGRAPH_STYLE,
        spacing,
      }
    case 'bullets':
      return { id, type: 'bullets', itemsText: '', align: DEFAULT_ALIGN.bullets, spacing }
    case 'image':
      return { id, type: 'image', ...mediaBlockFields(), spacing }
    case 'video':
      return { id, type: 'video', ...mediaBlockFields(), spacing }
    case 'cta':
      return { id, type: 'cta', text: '', link: '', style: 'primary', spacing }
    case 'card':
      return { id, type: 'card', heading: '', text: '', style: DEFAULT_CARD_STYLE, spacing }
  }
}

/** One row column of any kind, including the deliberate blank. */
export const rowChildDraft = (kind: RowChildKind): RowChildBlockDraft =>
  kind === 'empty' ? { id: nextBlockDraftId(), type: 'empty' } : emptyBlockDraft(kind)

/**
 * A new row holding exactly the columns the operator picked.
 *
 * The kinds are asked for up front rather than defaulted: a row arrives already
 * holding its children because an under-filled row is invalid against the
 * contract the moment it exists, and filling it with blank paragraphs guessed
 * wrong often enough that the operator's first act was to delete them. `empty`
 * is a real answer here, so there is no longer a neutral placeholder to fall
 * back on — which is what made the guess defensible before.
 */
export const rowBlockDraft = (kinds: readonly RowChildKind[]): RowBlockDraft => ({
  id: nextBlockDraftId(),
  type: 'row',
  spacing: DEFAULT_SPACING,
  children: kinds.map(rowChildDraft),
})

export const buttonPairRowDraft = (): RowBlockDraft => {
  const row = rowBlockDraft(['cta', 'cta'])

  return {
    ...row,
    layout: 'compact',
    gap: 'small',
    vertical_align: 'center',
    mobile_align: 'center',
    children: row.children.map((child, index) =>
      child.type === 'cta' && index === 1 ? { ...child, style: 'secondary' } : child,
    ),
  }
}

/**
 * One served media block as a draft, with its two URLs told apart.
 *
 * The API returns ONE `url` either way: the link for a link block, and a signed
 * GET for an uploaded one. Which it is is `upload_id`, so that is what decides
 * where the value lands — a signed URL parked in `url` would be sent straight
 * back on the next save and stored, and the modal would go blank within the hour.
 */
const mediaSourceFrom = (block: { url?: string; upload_id?: string }): MediaBlockSource => {
  const uploadId = block.upload_id ?? ''
  const served = block.url ?? ''

  return uploadId === ''
    ? linkedMediaSource(served)
    : { ...linkedMediaSource(), uploadId, storedUrl: served }
}

type StoredLeafBlock =
  | Exclude<AnnouncementAdminSchemaBlocksItem, { type: 'row' }>
  | AnnouncementRowBlockReadSchemaChildrenItem

function leafDraftFrom(
  block: Exclude<AnnouncementAdminSchemaBlocksItem, { type: 'row' }>,
): LeafBlockDraft
function leafDraftFrom(block: AnnouncementRowBlockReadSchemaChildrenItem): RowChildBlockDraft
function leafDraftFrom(block: StoredLeafBlock): LeafBlockDraft | EmptyBlockDraft {
  const id = nextBlockDraftId()
  const spacing = 'spacing' in block ? (block.spacing ?? DEFAULT_SPACING) : DEFAULT_SPACING

  switch (block.type) {
    case 'hero':
      return { id, type: 'hero', ...mediaSourceFrom(block), spacing }
    case 'heading':
      return {
        id,
        type: 'heading',
        text: block.text,
        highlight: block.highlight ?? '',
        align: block.align ?? DEFAULT_ALIGN.heading,
        size: block.size ?? DEFAULT_HEADING_SIZE,
        spacing,
      }
    case 'paragraph':
      return {
        id,
        type: 'paragraph',
        text: block.text,
        highlight: block.highlight ?? '',
        align: block.align ?? DEFAULT_ALIGN.paragraph,
        style: block.style ?? DEFAULT_PARAGRAPH_STYLE,
        spacing,
      }
    case 'bullets':
      return {
        id,
        type: 'bullets',
        itemsText: block.items.join('\n'),
        align: block.align ?? DEFAULT_ALIGN.bullets,
        spacing,
      }
    case 'image':
      return { id, type: 'image', ...mediaSourceFrom(block), ...mediaLayoutFrom(block), spacing }
    case 'video':
      return { id, type: 'video', ...mediaSourceFrom(block), ...mediaLayoutFrom(block), spacing }
    case 'cta':
      return {
        id,
        type: 'cta',
        text: block.text,
        link: block.link,
        style: block.style ?? 'primary',
        spacing,
      }
    case 'card':
      return {
        id,
        type: 'card',
        heading: block.heading,
        text: block.text,
        style: block.style ?? DEFAULT_CARD_STYLE,
        spacing,
      }
    case 'empty':
      return { id, type: 'empty' }
  }
}

/** The stored blocks as drafts the editor can hold. */
export const blockDraftsFrom = (
  blocks: readonly AnnouncementAdminSchemaBlocksItem[] | undefined,
): BlockDraft[] =>
  (blocks ?? []).map((block) =>
    block.type === 'row'
      ? {
          ...block,
          id: nextBlockDraftId(),
          type: 'row',
          spacing: block.spacing ?? DEFAULT_SPACING,
          children: block.children.map(leafDraftFrom),
        }
      : leafDraftFrom(block),
  )

/**
 * One bullet per non-blank line.
 *
 * Blank lines are dropped rather than kept as empty bullets, for the same reason
 * `parseList` drops them on the route prefixes: a trailing newline is how a
 * textarea feels to type in, not a request for one more empty item — and the
 * contract's per-item length cap would happily accept `''` and render a bullet
 * with nothing beside it.
 */
export const parseBulletItems = (itemsText: string): string[] =>
  itemsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')

/** What a just-finished upload is called, keyed by the block whose file produced it. */
export type BlockMediaUploadIds = Readonly<Record<BlockDraftId, string>>

/** One block's picked file, and which media endpoint kind it has to be presigned as. */
export type StagedBlockMedia = {
  id: BlockDraftId
  kind: AnnouncementMediaKindChoice
  file: File
}

/**
 * Every file the editor is holding that still has to reach the bucket, in draft order.
 *
 * Row columns are walked too: a picture inside a two-column row is as much a
 * pending upload as one at the top level, and missing it would save that block
 * pointing at nothing.
 */
export const stagedBlockMedia = (drafts: readonly BlockDraft[]): StagedBlockMedia[] =>
  drafts.flatMap((draft) =>
    draft.type === 'row' ? stagedLeafMedia(draft.children) : stagedLeafMedia([draft]),
  )

const stagedLeafMedia = (
  drafts: readonly (LeafBlockDraft | EmptyBlockDraft)[],
): StagedBlockMedia[] =>
  drafts.flatMap((draft) =>
    (draft.type === 'image' || draft.type === 'video' || draft.type === 'hero') &&
    draft.file !== null
      ? [{ id: draft.id, kind: MEDIA_KIND_BY_BLOCK[draft.type], file: draft.file }]
      : [],
  )

/**
 * Who the serialised blocks are for.
 *
 * `server` is the contract: an uploaded block sends its id and an empty url.
 * `preview` is the screen, which needs the opposite — a url that resolves right
 * now — and nothing serialised for it is ever sent.
 */
type PayloadTarget = 'server' | 'preview'

/**
 * The one source that reaches the target, out of the three a draft may hold.
 *
 * For the server a freshly uploaded id wins over everything: it is what the
 * operator just picked. The contract refuses a block carrying both a link and an
 * upload, so the loser is sent EMPTY rather than left as it was.
 */
const mediaPayloadFrom = (
  draft: MediaBlockDraft,
  uploaded: BlockMediaUploadIds,
  target: PayloadTarget,
): { url: string; upload_id: string } => {
  if (target === 'preview') {
    return { url: mediaPreviewUrl(draft), upload_id: '' }
  }

  const uploadId = uploaded[draft.id] ?? draft.uploadId

  return uploadId === ''
    ? { url: draft.url.trim(), upload_id: '' }
    : { url: '', upload_id: uploadId }
}

type TopLevelLeafPayload = Exclude<AnnouncementUpdateRequestBlocksItem, { type: 'row' }>

function leafPayloadFrom(
  draft: RowChildBlockDraft,
  uploaded: BlockMediaUploadIds,
  target: PayloadTarget,
): AnnouncementRowBlockSchemaChildrenItem
function leafPayloadFrom(
  draft: LeafBlockDraft,
  uploaded: BlockMediaUploadIds,
  target: PayloadTarget,
): TopLevelLeafPayload
function leafPayloadFrom(
  draft: LeafBlockDraft | EmptyBlockDraft,
  uploaded: BlockMediaUploadIds,
  target: PayloadTarget,
): AnnouncementRowBlockSchemaChildrenItem | TopLevelLeafPayload {
  // Sent on every block, default or not, for the reason `toBlocksPayload` gives.
  const spacing = draft.type === 'empty' ? DEFAULT_SPACING : (draft.spacing ?? DEFAULT_SPACING)

  switch (draft.type) {
    case 'hero':
      return { type: 'hero', ...mediaPayloadFrom(draft, uploaded, target), spacing }
    case 'heading':
      return {
        type: 'heading',
        text: draft.text.trim(),
        highlight: draft.highlight.trim(),
        align: draft.align,
        size: draft.size ?? DEFAULT_HEADING_SIZE,
        spacing,
      }
    case 'paragraph':
      return {
        type: 'paragraph',
        text: draft.text.trim(),
        highlight: (draft.highlight ?? '').trim(),
        align: draft.align,
        style: draft.style ?? DEFAULT_PARAGRAPH_STYLE,
        spacing,
      }
    case 'bullets':
      return {
        type: 'bullets',
        items: parseBulletItems(draft.itemsText),
        align: draft.align,
        spacing,
      }
    case 'image':
      return {
        type: 'image',
        ...mediaPayloadFrom(draft, uploaded, target),
        align: draft.align,
        width: draft.width,
        spacing,
      }
    case 'video':
      return {
        type: 'video',
        ...mediaPayloadFrom(draft, uploaded, target),
        align: draft.align,
        width: draft.width,
        spacing,
      }
    case 'cta':
      return {
        type: 'cta',
        text: draft.text.trim(),
        link: draft.link.trim(),
        style: draft.style,
        spacing,
      }
    case 'card':
      return {
        type: 'card',
        heading: draft.heading.trim(),
        text: draft.text.trim(),
        style: draft.style ?? DEFAULT_CARD_STYLE,
        spacing,
      }
    case 'empty':
      return { type: 'empty' }
  }
}

/**
 * The drafts as the contract wants them: ids stripped, text trimmed, bullets
 * split into the array the schema types.
 *
 * Every field is sent, including the ones whose value equals the contract
 * default. Omitting them would be smaller on the wire and wrong on the read
 * back: an operator who deliberately centres a bullet list gets `align: 'left'`
 * back from delivery's normalisation, and the editor would show them a choice
 * they did not make.
 */
export const toBlocksPayload = (
  drafts: readonly BlockDraft[],
  uploaded: BlockMediaUploadIds = {},
  target: PayloadTarget = 'server',
): AnnouncementUpdateRequestBlocksItem[] =>
  drafts.map((draft) => {
    if (draft.type !== 'row') {
      return leafPayloadFrom(draft, uploaded, target)
    }
    const { id: _id, children, ...layout } = draft

    return {
      ...layout,
      spacing: draft.spacing ?? DEFAULT_SPACING,
      children: children.map((child) => leafPayloadFrom(child, uploaded, target)),
    }
  })

/**
 * The same blocks, serialised for the screen instead of the wire.
 *
 * Previewing the wire shape unchanged would draw a hole where every uploaded
 * picture is: that shape sends the id and an empty url, on purpose.
 */
export const toPreviewBlocks = (
  drafts: readonly BlockDraft[],
): AnnouncementUpdateRequestBlocksItem[] => toBlocksPayload(drafts, {}, 'preview')

const orderedRowFields = (block: AnnouncementUpdateRequestBlocksItem) =>
  Object.fromEntries(Object.entries(block).sort(([left], [right]) => left.localeCompare(right)))

const comparableBlocks = (drafts: readonly BlockDraft[]) =>
  toBlocksPayload(drafts).map((block) =>
    block.type === 'row'
      ? orderedRowFields({
          ...block,
          layout: block.layout ?? MODAL_ROW_DEFAULTS.layout,
          gap: block.gap ?? MODAL_ROW_DEFAULTS.gap,
          vertical_align: block.vertical_align ?? MODAL_ROW_DEFAULTS.vertical_align,
          horizontal_align: block.horizontal_align ?? MODAL_ROW_DEFAULTS.horizontal_align,
          mobile_layout: block.mobile_layout ?? MODAL_ROW_DEFAULTS.mobile_layout,
          mobile_align: block.mobile_align ?? MODAL_ROW_DEFAULTS.mobile_align,
        })
      : block,
  )

/**
 * Whether two block lists differ in anything that reaches the server.
 *
 * BOTH sides are run through `toBlocksPayload` before they are compared, which
 * is what makes a string comparison safe here. Two things would otherwise report
 * a change on an announcement nobody touched, and `buildUpdatePatch` would then
 * PATCH `blocks` on every single save:
 *
 *   - Key ORDER. `JSON.stringify` is order-sensitive and the server's field order
 *     is its own business; serialising leaves and sorting the row fields makes
 *     the order identical regardless of where a layout field was first added.
 *   - Draft-only detail. Ids are minted fresh on every load, and `itemsText`
 *     holds the whitespace the payload drops.
 */
export const blocksChanged = (
  stored: readonly AnnouncementAdminSchemaBlocksItem[] | undefined,
  drafts: readonly BlockDraft[],
): boolean =>
  // A staged file is invisible to both projections — it has no id until the save
  // uploads it — so it is asked about separately. Without this a save that only
  // swapped a picture would report nothing to patch and silently drop the upload.
  stagedBlockMedia(drafts).length > 0 ||
  JSON.stringify(comparableBlocks(blockDraftsFrom(stored))) !==
    JSON.stringify(comparableBlocks(drafts))
